import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Copy, Link2, Loader2, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Clock, User, Hash, CornerDownLeft, ScanLine, Ban } from 'lucide-react';
import { Order, useOrders, orderBranchId } from './OrdersContext';
import { useBranches } from './BranchesContext';
// ---------------------------------------------------------------------------
// PROTOTYPE NOTE
// In production this parsing happens server-side: the backend fetches the CBE
// share link (mbreciept.cbe.com.et/v2-…) and scrapes the receipt, OR pulls the
// transaction from an official CBE API / statement feed. Here we *simulate*
// that fetch so the staff verification flow can be demoed end-to-end. The order
// itself is treated as the source of truth for what "the bank says".
// ---------------------------------------------------------------------------
type ParsedReceipt = {
  payerName: string;
  payeeSuffix: string;
  amount: number;
  currency: string;
  txnId: string;
  timestamp: number;
  status: 'completed' | 'failed';
};
type CheckResult = {
  key: string;
  label: string;
  detail: string;
  state: 'pass' | 'fail' | 'warn';
  critical: boolean;
};
const CBE_LINK_RE = /mbreciept\.cbe\.com\.et|cbe\.com\.et/i;
const REF_RE = /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{5,}$/;
function last4(num?: string) {
  if (!num) return '—';
  return num.slice(-4);
}
function formatTime(ts: number) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function CBETransferVerifier({
  order,
  onApprove
}: {
  order: Order;
  onApprove: () => void;
}) {
  const {
    orders,
    setPaymentStatus
  } = useOrders();
  const {
    branches
  } = useBranches();
  const bankBranch = branches.find(b => b.id === orderBranchId(order));
  const bankAccount = bankBranch?.bankAccounts?.find(a => a.id === order.selectedBankAccountId);
  const [input, setInput] = useState('');
  const [phase, setPhase] = useState<'idle' | 'fetching' | 'parsed'>('idle');
  const [receipt, setReceipt] = useState<ParsedReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [override, setOverride] = useState(false);
  const inputKind: 'link' | 'ref' | 'invalid' | 'empty' = useMemo(() => {
    const v = input.trim();
    if (!v) return 'empty';
    if (CBE_LINK_RE.test(v)) return 'link';
    if (REF_RE.test(v)) return 'ref';
    return 'invalid';
  }, [input]);
  // Simulated server-side fetch + parse of the CBE receipt.
  const handleVerify = () => {
    if (inputKind === 'empty' || inputKind === 'invalid') return;
    setError(null);
    setPhase('fetching');
    setReceipt(null);
    setOverride(false);
    window.setTimeout(() => {
      // The order is our stand-in for the bank's record of truth. A real
      // backend would read these fields off the fetched CBE receipt page.
      const txnId = inputKind === 'ref' ? input.trim().toUpperCase() : order.bankTransferRef || 'FT26161PZQC9';
      setReceipt({
        payerName: order.customerName || 'Account holder',
        payeeSuffix: last4(bankAccount?.accountNumber),
        amount: order.total,
        currency: 'ETB',
        txnId,
        timestamp: order.placedAt + 30 * 1000,
        status: 'completed'
      });
      setPhase('parsed');
    }, 850);
  };
  const checks: CheckResult[] = useMemo(() => {
    if (!receipt) return [];
    const amountOk = Math.abs(receipt.amount - order.total) < 0.01;
    const payeeOk = !!bankAccount && receipt.payeeSuffix === last4(bankAccount.accountNumber);
    // Reference uniqueness: same txn id must not already back a *different*
    // verified order.
    const reused = orders.some(o => o.id !== order.id && o.bankTransferRef && o.bankTransferRef.toUpperCase() === receipt.txnId.toUpperCase());
    const minutesApart = Math.abs(receipt.timestamp - order.placedAt) / 60000;
    const timeOk = minutesApart <= 120;
    return [{
      key: 'status',
      label: 'Transaction completed',
      detail: receipt.status === 'completed' ? 'Receipt status: success' : 'Receipt does not show success',
      state: receipt.status === 'completed' ? 'pass' : 'fail',
      critical: true
    }, {
      key: 'amount',
      label: 'Amount matches',
      detail: amountOk ? `ETB ${receipt.amount.toLocaleString()} = order total` : `Receipt ETB ${receipt.amount.toLocaleString()} ≠ expected ETB ${order.total.toLocaleString()}`,
      state: amountOk ? 'pass' : 'fail',
      critical: true
    }, {
      key: 'payee',
      label: 'Paid into your account',
      detail: bankAccount ? payeeOk ? `Account ending ${receipt.payeeSuffix}` : `Receipt account ••${receipt.payeeSuffix} ≠ your account ••${last4(bankAccount.accountNumber)}` : 'No destination account on file to compare',
      state: !bankAccount ? 'warn' : payeeOk ? 'pass' : 'fail',
      critical: !!bankAccount
    }, {
      key: 'unique',
      label: 'Reference not reused',
      detail: reused ? 'This transaction ID is already on another order' : `${receipt.txnId} used once`,
      state: reused ? 'fail' : 'pass',
      critical: true
    }, {
      key: 'time',
      label: 'Within time window',
      detail: timeOk ? `Paid around order time` : `Receipt is ${Math.round(minutesApart)} min from order — review`,
      state: timeOk ? 'pass' : 'warn',
      critical: false
    }];
  }, [receipt, order, bankAccount, orders]);
  const criticalFailures = checks.filter(c => c.critical && c.state === 'fail');
  const allCriticalPass = checks.length > 0 && criticalFailures.length === 0;
  const hasWarnings = checks.some(c => c.state === 'warn');
  const canApprove = allCriticalPass || override;
  const handleReject = () => {
    setPaymentStatus(order.id, 'failed');
  };
  return <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 mb-5" data-id="element-917">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3" data-id="element-918">
        <div className="w-8 h-8 rounded-lg bg-white shadow-sm text-indigo-600 flex items-center justify-center" data-id="element-919">
          <Building2 size={16} data-id="element-920" />
        </div>
        <div className="min-w-0" data-id="element-921">
          <div className="text-[11px] font-bold uppercase tracking-wide text-indigo-700" data-id="element-922">
            Bank transfer · verify with CBE receipt
          </div>
          <div className="text-[11px] text-indigo-600/80" data-id="element-923">
            Paste the customer's CBE share link or transfer reference.
          </div>
        </div>
      </div>

      {/* Expected payment summary */}
      <div className="space-y-2.5 bg-white rounded-lg border border-indigo-100 p-3" data-id="element-924">
        <div className="flex items-center justify-between gap-3" data-id="element-925">
          <span className="text-xs text-gray-500" data-id="element-926">Expected amount</span>
          <span className="text-sm font-extrabold text-gray-900 tabular-nums" data-id="element-927">
            ETB {order.total.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3" data-id="element-928">
          <span className="text-xs text-gray-500" data-id="element-929">Order reference</span>
          <span className="text-sm font-bold text-gray-900" data-id="element-930">{order.id}</span>
        </div>
        {bankAccount && <div className="pt-1.5 border-t border-gray-100" data-id="element-931">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-932">
              Should be paid into
            </div>
            <div className="text-sm font-semibold text-gray-900" data-id="element-933">
              {bankAccount.bankName}
              {bankAccount.label ? ` · ${bankAccount.label}` : ''}
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5" data-id="element-934">
              <span className="text-sm text-gray-700 tabular-nums" data-id="element-935">
                {bankAccount.accountNumber}
              </span>
              <button onClick={() => navigator.clipboard?.writeText(bankAccount.accountNumber)} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Copy account number" data-id="element-936">
                <Copy size={13} data-id="element-937" />
              </button>
            </div>
          </div>}
        {order.bankTransferRef && <div className="pt-1.5 border-t border-gray-100" data-id="element-938">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-939">
              Customer-submitted reference
            </div>
            <div className="flex items-center justify-between gap-2" data-id="element-940">
              <span className="text-sm font-semibold text-gray-900 break-all" data-id="element-941">
                {order.bankTransferRef}
              </span>
              <button onClick={() => setInput(order.bankTransferRef || '')} className="flex-shrink-0 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1" data-id="element-942">
                <CornerDownLeft size={12} data-id="element-943" />
                Use
              </button>
            </div>
          </div>}
      </div>

      {/* Input */}
      <div className="mt-3" data-id="element-944">
        <label className="block text-[11px] font-semibold text-indigo-800 mb-1.5" data-id="element-945">
          CBE share link or reference ID
        </label>
        <div className="relative" data-id="element-946">
          <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-947" />
          <input value={input} onChange={e => {
          setInput(e.target.value);
          if (phase !== 'idle') {
            setPhase('idle');
            setReceipt(null);
          }
        }} placeholder="https://mbreciept.cbe.com.et/v2-…  or  FT26161PZQC9" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-indigo-200 text-sm bg-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" data-id="element-948" />
        </div>
        {inputKind === 'invalid' && <div className="text-[11px] text-amber-600 font-medium mt-1.5 flex items-center gap-1" data-id="element-949">
            <AlertTriangle size={12} data-id="element-950" />
            That doesn't look like a CBE link or a valid reference.
          </div>}
        {inputKind === 'link' && <div className="text-[11px] text-indigo-600 font-medium mt-1.5 flex items-center gap-1" data-id="element-951">
            <ScanLine size={12} data-id="element-952" />
            CBE receipt link detected — we'll fetch and parse it.
          </div>}
        <button onClick={handleVerify} disabled={inputKind === 'empty' || inputKind === 'invalid' || phase === 'fetching'} className="w-full mt-2.5 px-3 py-2.5 rounded-lg text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed" data-id="element-953">
          {phase === 'fetching' ? <>
              <Loader2 size={15} className="animate-spin" data-id="element-954" />
              Fetching receipt…
            </> : <>
              <ScanLine size={15} data-id="element-955" />
              Fetch &amp; verify
            </>}
        </button>
      </div>

      {/* Parsed receipt + checks */}
      <AnimatePresence data-id="element-956">
        {phase === 'parsed' && receipt && <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="mt-3 space-y-3" data-id="element-957">
            {/* CBE-styled receipt card */}
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-white" data-id="element-958">
              <div className="bg-[#5b2c91] px-4 py-3 flex items-center gap-2" data-id="element-959">
                <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white" data-id="element-960">
                  <CheckCircle2 size={16} data-id="element-961" />
                </div>
                <div data-id="element-962">
                  <div className="text-white font-bold text-sm leading-tight" data-id="element-963">
                    Transaction Completed
                  </div>
                  <div className="text-white/80 text-[11px] leading-tight" data-id="element-964">
                    Commercial Bank of Ethiopia
                  </div>
                </div>
              </div>
              <div className="p-3 space-y-2" data-id="element-965">
                <ReceiptRow icon={<User size={12} data-id="element-967" />} label="Payer" value={receipt.payerName} data-id="element-966" />
                <ReceiptRow icon={<Building2 size={12} data-id="element-969" />} label="Paid into" value={`Account ••${receipt.payeeSuffix}`} data-id="element-968" />
                <ReceiptRow icon={<Hash size={12} data-id="element-971" />} label="Transaction ID" value={receipt.txnId} mono data-id="element-970" />
                <ReceiptRow icon={<Clock size={12} data-id="element-973" />} label="Date" value={formatTime(receipt.timestamp)} data-id="element-972" />
                <div className="flex items-center justify-between pt-2 border-t border-gray-100" data-id="element-974">
                  <span className="text-xs text-gray-500" data-id="element-975">Amount</span>
                  <span className="text-base font-extrabold text-gray-900 tabular-nums" data-id="element-976">
                    {receipt.currency} {receipt.amount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification checks */}
            <div className="rounded-xl border border-gray-200 bg-white p-3" data-id="element-977">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2" data-id="element-978">
                Verification checks
              </div>
              <div className="space-y-2" data-id="element-979">
                {checks.map(c => <div key={c.key} className="flex items-start gap-2.5" data-id="element-980">
                    <span className="mt-0.5 flex-shrink-0" data-id="element-981">
                      {c.state === 'pass' && <CheckCircle2 size={16} className="text-emerald-500" data-id="element-982" />}
                      {c.state === 'fail' && <XCircle size={16} className="text-rose-500" data-id="element-983" />}
                      {c.state === 'warn' && <AlertTriangle size={16} className="text-amber-500" data-id="element-984" />}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-985">
                      <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5" data-id="element-986">
                        {c.label}
                        {c.critical && <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-987">
                            required
                          </span>}
                      </div>
                      <div className={`text-[11px] ${c.state === 'fail' ? 'text-rose-600' : c.state === 'warn' ? 'text-amber-600' : 'text-gray-500'}`} data-id="element-988">
                        {c.detail}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Verdict + actions */}
            {allCriticalPass ? <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 font-semibold flex items-center gap-1.5" data-id="element-989">
                <ShieldCheck size={14} data-id="element-990" />
                {hasWarnings ? 'All required checks passed (with warnings). Safe to approve.' : 'All required checks passed. Safe to approve.'}
              </div> : <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2" data-id="element-991">
                <div className="text-[12px] text-rose-700 font-semibold flex items-center gap-1.5 mb-1.5" data-id="element-992">
                  <AlertTriangle size={14} data-id="element-993" />
                  {criticalFailures.length} required check
                  {criticalFailures.length === 1 ? '' : 's'} failed — do not
                  approve unless you've confirmed manually.
                </div>
                <label className="flex items-center gap-2 text-[11px] text-rose-700 font-medium cursor-pointer" data-id="element-994">
                  <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} className="w-3.5 h-3.5 rounded border-rose-300 text-rose-600 focus:ring-rose-400" data-id="element-995" />
                  I've verified this transfer manually and accept the risk.
                </label>
              </div>}

            <div className="flex gap-2" data-id="element-996">
              <button onClick={onApprove} disabled={!canApprove} className="flex-1 px-3 py-2.5 rounded-lg text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 shadow-sm disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed" data-id="element-997">
                <ShieldCheck size={15} data-id="element-998" />
                Approve payment
              </button>
              <button onClick={handleReject} className="px-3 py-2.5 rounded-lg text-sm font-bold bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center gap-1.5" data-id="element-999">
                <Ban size={15} data-id="element-1000" />
                Reject
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function ReceiptRow({
  icon,
  label,
  value,
  mono
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-1001">
      <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-1002">
        <span className="text-gray-400" data-id="element-1003">{icon}</span>
        {label}
      </span>
      <span className={`text-sm font-semibold text-gray-900 text-right break-all ${mono ? 'font-mono tracking-tight' : ''}`} data-id="element-1004">
        {value}
      </span>
    </div>;
}